// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Snapping for the Ground's measure tools and Placér 360° (frontend-aa2,
 * frontend-4rt): the building model's walls and openings (`GET /walls`,
 * `/openings`) as snap features — wall axis ends and face corners
 * ("hjørne"), the faces themselves ("væg"), the openings' jambs
 * ("åbning") — and the ranking of everything within reach of the pointer,
 * the scan's surface (cloud points, mesh corners) included. Pure: the
 * scene's projection is passed in.
 *
 * The rule (the user's): BIM features win over mesh features, which win
 * over cloud points, whenever several are within the radius; within a tier
 * a point beats a line, then the nearest on screen wins.
 */

import type { SolidOpening, SolidWall } from '../api/types';
import type { Vec3 } from './ruler';

export type SnapKind = 'punkt' | 'hjørne' | 'midtpunkt' | 'væg' | 'åbning';
export type SnapSource = 'bim' | 'mesh' | 'cloud';

/** What the indicator says, by kind. */
export const SNAP_LABEL: Record<SnapKind, string> = {
  punkt: 'punkt',
  hjørne: 'hjørne',
  midtpunkt: 'midtpunkt',
  væg: 'væg',
  åbning: 'åbning',
};

/** The default reach, CSS px. */
export const SNAP_RADIUS_PX = 10;

/** A snap target: a point, or a segment (`b`) the pointer slides along. */
export interface SnapFeature {
  kind: SnapKind;
  a: Vec3;
  b?: Vec3;
}

export interface SnapCandidate {
  kind: SnapKind;
  source: SnapSource;
  /** World point, metres. */
  world: Vec3;
  /** Distance to the pointer on screen, CSS px. */
  px: number;
  /** Along the pick ray from its origin, metres; null when unknown. */
  depth: number | null;
}

export interface Ray {
  origin: Vec3;
  /** Unit direction. */
  dir: Vec3;
}

/** World points (xyz stride 3) → canvas px (xy stride 2, `NaN` off screen): `ViewportHandle.projectWorld`. */
export type Project = (xyz: Float64Array) => Float32Array;

// ------------------------------------------------------------- features --

export interface BimFeatureOptions {
  /**
   * The locked plan's floor (world z): every feature is laid flat at it, and
   * only the walls standing on that floor count. Null in 3D: features at
   * their real heights (base and top, sill and head).
   */
  planZ: number | null;
  /** In the plan: only this storey's walls and openings (null = by height). */
  storeyId?: number | null;
}

/** A wall's unit direction and left normal in x/y; null for a zero-length wall. */
function wallFrame(w: SolidWall): { d: [number, number]; n: [number, number]; len: number } | null {
  const dx = w.end[0] - w.start[0];
  const dy = w.end[1] - w.start[1];
  const len = Math.hypot(dx, dy);
  if (!(len > 1e-6)) return null;
  return { d: [dx / len, dy / len], n: [-dy / len, dx / len], len };
}

/** Whether a wall stands on the plan's floor. */
function onPlan(w: { z_base: number; z_top: number; storey_id: number | null }, opts: BimFeatureOptions): boolean {
  if (opts.planZ === null) return true;
  if (opts.storeyId !== undefined && opts.storeyId !== null && w.storey_id !== null) return w.storey_id === opts.storeyId;
  return w.z_base <= opts.planZ + 1.2 && w.z_top >= opts.planZ + 0.2;
}

/** A wall's footprint for the corners: its frame, half thickness and how far each end runs on into a wall it joins. */
interface Footprint {
  w: SolidWall;
  a: [number, number];
  d: [number, number];
  n: [number, number];
  len: number;
  /** Half the thickness. */
  t: number;
  /** How far the footprint runs on past the start / end: the half thickness of the wall that end is joined to, else 0. */
  extS: number;
  extE: number;
}

/** Whether two walls stand side by side in height (they share more than a centimetre). */
const zOverlap = (p: SolidWall, q: SolidWall) => Math.min(p.z_top, q.z_top) - Math.max(p.z_base, q.z_base) > 0.01;

/** Distance from a point to segment a–b in x/y. */
function distToSegment(p: readonly [number, number], a: readonly [number, number], b: readonly [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const s = l2 > 0 ? Math.min(1, Math.max(0, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return Math.hypot(p[0] - (a[0] + s * dx), p[1] - (a[1] + s * dy));
}

/**
 * The thick walls' footprints. An end that stops inside another wall (its
 * axis end within that wall's half thickness of its axis, plus a
 * centimetre) is joined: the footprint runs on by that wall's half
 * thickness, so the union closes over the joint — an L's outer corner, at
 * either thickness, comes out where the two outer faces meet.
 */
function footprints(walls: readonly SolidWall[]): Footprint[] {
  const out: Footprint[] = [];
  for (const w of walls) {
    const f = wallFrame(w);
    const t = (w.thickness ?? 0) / 2;
    if (!f || !(t > 0)) continue;
    out.push({ w, a: [w.start[0], w.start[1]], d: f.d, n: f.n, len: f.len, t, extS: 0, extE: 0 });
  }
  for (const p of out) {
    for (const q of out) {
      if (p === q || !zOverlap(p.w, q.w)) continue;
      const qa: [number, number] = q.a;
      const qb: [number, number] = [q.w.end[0], q.w.end[1]];
      if (distToSegment(p.w.start, qa, qb) <= q.t + 0.01) p.extS = Math.max(p.extS, q.t);
      if (distToSegment(p.w.end, qa, qb) <= q.t + 0.01) p.extE = Math.max(p.extE, q.t);
    }
  }
  return out;
}

/** Whether (x, y) is inside a footprint (its edges included). */
function inside(f: Footprint, x: number, y: number): boolean {
  const rx = x - f.a[0];
  const ry = y - f.a[1];
  const s = rx * f.d[0] + ry * f.d[1];
  const o = rx * f.n[0] + ry * f.n[1];
  return s >= -f.extS - 1e-9 && s <= f.len + f.extE + 1e-9 && Math.abs(o) <= f.t + 1e-9;
}

/** How far either side of a candidate corner the union is sampled, metres. */
const CORNER_EPS = 0.004;

/**
 * Whether (x, y) is a vertex of the union of `fps`: of the four points a
 * hair away along ±`u` ±`v` (the two edges meeting there), one or three are
 * inside — a convex or a concave corner — or two diagonally opposite (two
 * corners touching). Two side by side is a point on an edge; none or four,
 * not on the outline.
 */
function isUnionCorner(fps: readonly Footprint[], x: number, y: number, u: readonly [number, number], v: readonly [number, number]): boolean {
  const ins = [
    [1, 1],
    [1, -1],
    [-1, -1],
    [-1, 1],
  ].map(([su, sv]) => {
    const px = x + CORNER_EPS * (su * u[0] + sv * v[0]);
    const py = y + CORNER_EPS * (su * u[1] + sv * v[1]);
    return fps.some((f) => inside(f, px, py));
  });
  const n = ins.filter(Boolean).length;
  if (n === 1 || n === 3) return true;
  return n === 2 && ins[0] === ins[2];
}

/** Where segments a1–b1 and a2–b2 cross (ends included, to a tenth of a millimetre); null if they do not, or run parallel. */
function crossing(
  a1: readonly [number, number],
  b1: readonly [number, number],
  a2: readonly [number, number],
  b2: readonly [number, number],
): [number, number] | null {
  const rx = b1[0] - a1[0];
  const ry = b1[1] - a1[1];
  const sx = b2[0] - a2[0];
  const sy = b2[1] - a2[1];
  const denom = rx * sy - ry * sx;
  const lp = Math.hypot(rx, ry);
  const lq = Math.hypot(sx, sy);
  if (!(lp > 1e-9 && lq > 1e-9) || Math.abs(denom) < 0.01 * lp * lq) return null;
  const qx = a2[0] - a1[0];
  const qy = a2[1] - a1[1];
  const u = (qx * sy - qy * sx) / denom;
  const v = (qx * ry - qy * rx) / denom;
  const slackU = 1e-4 / lp;
  const slackV = 1e-4 / lq;
  if (u < -slackU || u > 1 + slackU || v < -slackV || v > 1 + slackV) return null;
  return [a1[0] + u * rx, a1[1] + u * ry];
}

/** A footprint's two (run-on) faces: start and end corner, for +n and −n. */
function facesOf(f: Footprint): [[number, number], [number, number]][] {
  const s: [number, number] = [f.a[0] - f.d[0] * f.extS, f.a[1] - f.d[1] * f.extS];
  const e: [number, number] = [f.a[0] + f.d[0] * (f.len + f.extE), f.a[1] + f.d[1] * (f.len + f.extE)];
  return [1, -1].map((k) => [
    [s[0] + k * f.n[0] * f.t, s[1] + k * f.n[1] * f.t],
    [e[0] + k * f.n[0] * f.t, e[1] + k * f.n[1] * f.t],
  ]) as [[number, number], [number, number]][];
}

/**
 * The walls and openings as snap features.
 *
 * - "hjørne": the corners of the walls' outline — the vertices of the union
 *   of their footprints (`isUnionCorner`), so an L gives its inner and outer
 *   corner, a T the two room corners beside its stem and nothing on the far
 *   face, a free end its two face corners. A wall of unknown thickness
 *   gives its axis ends.
 * - "midtpunkt": the middle of a free wall end (its axis end).
 * - "væg": each face (or the axis, without a thickness).
 * - "åbning": an opening's jambs on both faces of its host.
 *
 * In 3D each comes at the walls' base and top (an opening's sill and
 * head), plus the vertical corner edges; only walls standing side by side
 * in height make each other's corners.
 */
export function bimFeatures(walls: readonly SolidWall[], openings: readonly SolidOpening[], opts: BimFeatureOptions): SnapFeature[] {
  const out: SnapFeature[] = [];
  const plan = opts.planZ !== null;
  const byGuid = new Map(walls.map((w) => [w.guid, w]));
  const shown = walls.filter((w) => onPlan(w, opts) && wallFrame(w) !== null);
  const fps = footprints(shown);
  const fpOf = new Map(fps.map((f) => [f.w, f]));
  const levels = (zBase: number, zTop: number) => (plan ? [opts.planZ as number] : [zBase, zTop]);
  const corner = (x: number, y: number, zBase: number, zTop: number) => {
    for (const z of levels(zBase, zTop)) out.push({ kind: 'hjørne', a: [x, y, z] });
    if (!plan) out.push({ kind: 'væg', a: [x, y, zBase], b: [x, y, zTop] });
  };

  for (const w of shown) {
    const f = wallFrame(w)!;
    const fp = fpOf.get(w);
    const ends: [number, number][] = [
      [w.start[0], w.start[1]],
      [w.end[0], w.end[1]],
    ];
    if (!fp) {
      // No thickness: the axis is all there is.
      for (const [x, y] of ends) for (const z of levels(w.z_base, w.z_top)) out.push({ kind: 'hjørne', a: [x, y, z] });
      for (const z of levels(w.z_base, w.z_top)) out.push({ kind: 'væg', a: [...ends[0], z], b: [...ends[1], z] });
      continue;
    }
    const near = fps.filter((g) => zOverlap(g.w, w));
    // The faces as drawn, and the run-on footprint's corners where they are the outline's.
    for (const k of [1, -1]) {
      const a: [number, number] = [ends[0][0] + k * f.n[0] * fp.t, ends[0][1] + k * f.n[1] * fp.t];
      const b: [number, number] = [ends[1][0] + k * f.n[0] * fp.t, ends[1][1] + k * f.n[1] * fp.t];
      for (const z of levels(w.z_base, w.z_top)) out.push({ kind: 'væg', a: [...a, z], b: [...b, z] });
    }
    for (const face of facesOf(fp)) {
      for (const [x, y] of face) if (isUnionCorner(near, x, y, f.d, f.n)) corner(x, y, w.z_base, w.z_top);
    }
    // A free end's middle.
    if (fp.extS === 0) for (const z of levels(w.z_base, w.z_top)) out.push({ kind: 'midtpunkt', a: [...ends[0], z] });
    if (fp.extE === 0) for (const z of levels(w.z_base, w.z_top)) out.push({ kind: 'midtpunkt', a: [...ends[1], z] });
  }

  // Where two walls' faces cross: a room's corners at a T, or where a wall
  // runs through another — kept only where the outline turns.
  for (let i = 0; i < fps.length; i += 1) {
    for (let j = i + 1; j < fps.length; j += 1) {
      const p = fps[i];
      const q = fps[j];
      if (!zOverlap(p.w, q.w)) continue;
      const zBase = Math.max(p.w.z_base, q.w.z_base);
      const zTop = Math.min(p.w.z_top, q.w.z_top);
      const near = fps.filter((g) => Math.min(g.w.z_top, zTop) - Math.max(g.w.z_base, zBase) > 0.01);
      for (const fa of facesOf(p)) {
        for (const fb of facesOf(q)) {
          const hit = crossing(fa[0], fa[1], fb[0], fb[1]);
          if (!hit) continue;
          // A face corner is already in (the ends above).
          const atEnd = [fa[0], fa[1], fb[0], fb[1]].some((e) => Math.hypot(e[0] - hit[0], e[1] - hit[1]) < 1e-4);
          if (atEnd) continue;
          if (isUnionCorner(near, hit[0], hit[1], p.d, q.d)) corner(hit[0], hit[1], zBase, zTop);
        }
      }
    }
  }

  for (const o of openings) {
    if (!o.start || !o.end) continue;
    const host = byGuid.get(o.wall_guid);
    if (plan && host && !onPlan(host, opts)) continue;
    if (plan && !host && opts.storeyId !== undefined && opts.storeyId !== null && o.storey_id !== null && o.storey_id !== opts.storeyId) continue;
    const f = host ? wallFrame(host) : null;
    const t = host ? (host.thickness ?? 0) / 2 : 0;
    const offsets = f && t > 0 ? [t, -t] : [0];
    const zs = plan ? [opts.planZ as number] : [o.z_sill, o.z_head];
    for (const z of zs) {
      for (const jamb of [o.start, o.end]) {
        for (const s of offsets) {
          const nx = f ? f.n[0] * s : 0;
          const ny = f ? f.n[1] * s : 0;
          out.push({ kind: 'åbning', a: [jamb[0] + nx, jamb[1] + ny, z] });
        }
      }
    }
  }
  // Two walls meeting give their shared corner twice: once is enough.
  const seen = new Set<string>();
  return out.filter((f) => {
    if (f.b) return true;
    const key = `${f.kind}:${f.a.map((c) => Math.round(c * 1e4)).join(',')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// --------------------------------------------------------------- picking --

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** The point of segment a–b nearest the ray's line (its parameter clamped to the segment). */
export function closestOnSegmentToRay(a: Vec3, b: Vec3, ray: Ray): Vec3 {
  const d1 = sub(b, a);
  const r = sub(a, ray.origin);
  const A = dot(d1, d1);
  if (!(A > 1e-12)) return a;
  const B = dot(d1, ray.dir);
  const C = dot(ray.dir, ray.dir);
  const D = dot(d1, r);
  const E = dot(ray.dir, r);
  const denom = A * C - B * B;
  // Parallel (a wall seen end-on): the end nearer the ray's origin.
  let s = Math.abs(denom) < 1e-12 ? 0 : (B * E - C * D) / denom;
  s = Math.min(1, Math.max(0, s));
  return [a[0] + s * d1[0], a[1] + s * d1[1], a[2] + s * d1[2]];
}

/** Each feature within `radius` px of the pointer, as a candidate. */
export function featureCandidates(
  features: readonly SnapFeature[],
  pointer: readonly [number, number],
  ray: Ray,
  project: Project,
  radius: number,
  source: SnapSource = 'bim',
): SnapCandidate[] {
  if (features.length === 0) return [];
  const world = features.map((f) => (f.b ? closestOnSegmentToRay(f.a, f.b, ray) : f.a));
  const xyz = new Float64Array(world.length * 3);
  world.forEach((p, i) => xyz.set(p, i * 3));
  const screen = project(xyz);
  const out: SnapCandidate[] = [];
  for (let i = 0; i < world.length; i += 1) {
    const sx = screen[i * 2];
    const sy = screen[i * 2 + 1];
    if (!Number.isFinite(sx) || !Number.isFinite(sy)) continue;
    const px = Math.hypot(sx - pointer[0], sy - pointer[1]);
    if (px > radius) continue;
    out.push({ kind: features[i].kind, source, world: world[i], px, depth: dot(sub(world[i], ray.origin), ray.dir) });
  }
  return out;
}

/** Lower wins: the tier of a candidate (BIM over mesh over cloud; a point over a line). */
export function snapTier(c: Pick<SnapCandidate, 'kind' | 'source'>): number {
  if (c.source === 'bim') return c.kind === 'væg' ? 1 : 0;
  if (c.source === 'mesh') return c.kind === 'midtpunkt' ? 3 : 2;
  return 4;
}

/**
 * How far behind the visible surface a BIM feature may lie and still snap
 * (metres): a wall corner is on the surface give or take the scan's noise
 * and the model's fit; one in the next room is not.
 */
export const OCCLUSION_SLACK = 0.35;

/**
 * The best candidate within `radius`: the lowest tier, then the nearest on
 * screen. `surfaceDepth` (3D: the depth of the scan or mesh under the
 * pointer) hides BIM features behind it; null in the plan, where every
 * wall is seen from above.
 */
export function rankSnap(candidates: readonly SnapCandidate[], radius: number, surfaceDepth: number | null = null): SnapCandidate | null {
  let best: SnapCandidate | null = null;
  for (const c of candidates) {
    if (!(c.px <= radius)) continue;
    if (c.source === 'bim' && surfaceDepth !== null && c.depth !== null && c.depth > surfaceDepth + OCCLUSION_SLACK) continue;
    if (!best) {
      best = c;
      continue;
    }
    const dt = snapTier(c) - snapTier(best);
    if (dt < 0 || (dt === 0 && c.px < best.px)) best = c;
  }
  return best;
}

/**
 * The corners and edge midpoints of a mesh triangle worth snapping to: all
 * three corners, and the midpoints of its edges except the one opposite a
 * right angle — a quad's diagonal, whose midpoint is the middle of a face,
 * not of an edge.
 */
export function triangleFeatures(a: Vec3, b: Vec3, c: Vec3): SnapFeature[] {
  const out: SnapFeature[] = [a, b, c].map((p) => ({ kind: 'hjørne' as const, a: p }));
  const corners = [a, b, c];
  for (let i = 0; i < 3; i += 1) {
    const p = corners[i];
    const q = corners[(i + 1) % 3];
    const opp = corners[(i + 2) % 3];
    const u = sub(p, opp);
    const v = sub(q, opp);
    const lu = Math.sqrt(dot(u, u));
    const lv = Math.sqrt(dot(v, v));
    // cos of the angle at the opposite corner: ~0 is a right angle.
    if (lu > 0 && lv > 0 && Math.abs(dot(u, v) / (lu * lv)) < 0.02) continue;
    out.push({ kind: 'midtpunkt', a: [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2, (p[2] + q[2]) / 2] });
  }
  return out;
}
